import {useEffect} from 'react';

interface PersistableActor {
  subscribe: (listener: () => void) => {unsubscribe: () => void};
  getPersistedSnapshot: () => unknown;
}

// Subscribes to an xstate actor and writes its persisted snapshot through
// `write` on every transition. Pairs with hydration via createActor's
// `snapshot` option at mount — together they let a form survive page reloads.
// Writes are best-effort: if the sink throws (quota exceeded, storage
// disabled), the in-memory actor stays the source of truth and the user's
// session continues uninterrupted.
export const usePersistActorSnapshot = (
  actor: PersistableActor,
  write: (snapshot: unknown) => void,
): void => {
  // effect:audited — syncs with xstate actor's pub/sub; needs unsubscribe
  // cleanup on unmount and on actor/write change.
  useEffect(() => {
    const subscription = actor.subscribe(() => {
      try {
        write(actor.getPersistedSnapshot());
      } catch {
        // Persistence is best-effort; the in-memory actor remains the source
        // of truth if the write strategy throws.
      }
    });

    return () => subscription.unsubscribe();
  }, [actor, write]);
};
